import { viteAppBaseUrl } from '@/constants/env';
import { FF_CONSULTATIONS_EXPERIMENT_RINGTONE } from '@/constants/featureFlags';
import settings from '@/data/constants';
import { getUser, isDoctor } from '@/data/service/authService';
import { useFeatureFlags } from '@/hooks/flags/useFeatureFlags';
import { usePatientPhoneNumber } from '@/hooks/usePatientPhoneNumber';
import { AmazonConnectCcpProvider } from '@montugroup/clinic-telecom/AmazonConnectCcpProvider';
import { ConfigProvider as TelecomConfigProvider } from '@montugroup/clinic-telecom/ConfigProvider';
import type { ReactNode } from 'react';
import { useUpdatePatientDetails } from '../Patient/hooks/useUpdatePatientDetails';
import { getPmsRingtoneUrl } from './AmazonConnect.utils';

export type AmazonConnectProviderProps = {
  children: ReactNode;
};

export const AmazonConnectProvider = ({ children }: AmazonConnectProviderProps) => {
  const { shouldDisplayPatientPhoneNumber } = usePatientPhoneNumber();
  const baseUrl = viteAppBaseUrl();
  const user = getUser();
  const { flags } = useFeatureFlags();
  const customRingtone = flags?.[FF_CONSULTATIONS_EXPERIMENT_RINGTONE];
  const { mutateAsync: updatePatientDetail } = useUpdatePatientDetails('patient-basic-info');
  const assignDoctorToPatient = async (patientId: number) => {
    if (!isDoctor()) {
      return;
    }
    if (!user?.id) {
      throw new Error('Cannot assign patient to doctor: no authenticated user id');
    }
    return await updatePatientDetail(Number(patientId), { doctor: { userId: user.id } });
  };

  return (
    <TelecomConfigProvider baseUrl={baseUrl}>
      <AmazonConnectCcpProvider
        ccpUrl={settings.connect.ccpInstanceUrl}
        loginUrl={settings.connect.samlLoginUrl}
        region={settings.connect.region}
        supportUrl={settings.serviceDeskUrl}
        shouldDisplayPatientPhoneNumber={shouldDisplayPatientPhoneNumber}
        assignDoctorToPatient={assignDoctorToPatient}
        isDoctor={isDoctor()}
        ringtoneUrl={getPmsRingtoneUrl(customRingtone)}
      >
        {children}
      </AmazonConnectCcpProvider>
    </TelecomConfigProvider>
  );
};
